"use client";

import { useState, type ReactNode } from "react";
import { useTranslations, useLocale, useFormatter } from "use-intl";

import { getCompanyBadgeIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { BioText } from "@/components/bio-text";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EntityHero } from "@/components/catalog-entity/entity-hero";
import { EntityQuickLinks } from "@/components/catalog-entity/entity-quick-links";
import { CountryFlag } from "@/components/country-flag";
import { FallbackImage } from "@/components/fallback-image";
import { Link } from "@/components/link";
import { RichTextHtml } from "@/components/rich-text-html";
import { ShareButton } from "@/components/share-button";
import { WikipediaSection } from "@/components/wikipedia-section";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";
import { formatCountry } from "@garimpa/lib/countries";
import { buildImageSrcSet, withImageRenderParams } from "@garimpa/lib/imageUrls";
import type { CatalogUrl, SocialLink as SocialLinkType } from "@garimpa/lib/musicTypes";

const ALIAS_PREVIEW_COUNT = 6;
const BADGE_PREVIEW_COUNT = 4;

interface CompanyTaxonomyBadge {
  key: string;
  label: string;
  confidence: number;
}

interface CompanyRoleUsageBadge {
  roleCategory: string;
  distinctWorks: number;
}

interface CompanyHeaderProps {
  name: string;
  aliases: string[];
  parentName: string | null;
  parentId: string | null;
  officialUrls: CatalogUrl[];
  socialUrls: SocialLinkType[];
  imageUrl: string | null;
  profile: string | null;
  profileHtml: string | null;
  wikidataId: string | null;
  shortDescription: string | null;
  foundedYear: number | null;
  countryCodes: string[];
  usageCategories: string[];
  primaryUsageCategory: string | null;
  roleUsage: CompanyRoleUsageBadge[];
  taxonomy: CompanyTaxonomyBadge[];
  /** Related organizations and links cards (see `CompanyAbout`). */
  aside?: ReactNode;
}

interface CompanyBadge {
  key: string;
  label: string;
  icon: AppIcon;
}

function badgeIconForKey(key: string) {
  return getCompanyBadgeIcon(key);
}

function addBadge(badges: CompanyBadge[], seen: Set<string>, badge: CompanyBadge) {
  const normalized = `${badge.key}:${badge.label}`.toLowerCase();
  if (seen.has(normalized)) return;
  seen.add(normalized);
  badges.push(badge);
}

function buildCompanyBadges(input: {
  usageCategories: string[];
  primaryUsageCategory: string | null;
  roleUsage: CompanyRoleUsageBadge[];
  taxonomy: CompanyTaxonomyBadge[];
  formatLabel: (key: string, fallback?: string) => string;
}) {
  const badges: CompanyBadge[] = [];
  const seen = new Set<string>();
  const taxonomyKeys = new Set(input.taxonomy.map((taxonomy) => taxonomy.key));
  const canUseFallbackCategory = (category: string) =>
    !["venue", "festival"].includes(category) || taxonomyKeys.has(category);

  for (const taxonomy of [...input.taxonomy].sort(
    (a, b) => b.confidence - a.confidence || a.key.localeCompare(b.key),
  )) {
    addBadge(badges, seen, {
      key: taxonomy.key,
      label: input.formatLabel(taxonomy.key, taxonomy.label),
      icon: badgeIconForKey(taxonomy.key),
    });
  }

  const usageCategories = [input.primaryUsageCategory, ...input.usageCategories].filter(
    (category): category is string => Boolean(category),
  );

  for (const category of usageCategories) {
    if (category === "release_label_ref") continue;
    if (!canUseFallbackCategory(category)) continue;
    addBadge(badges, seen, {
      key: category,
      label: input.formatLabel(category),
      icon: badgeIconForKey(category),
    });
  }

  for (const usage of [...input.roleUsage].sort(
    (a, b) => b.distinctWorks - a.distinctWorks || a.roleCategory.localeCompare(b.roleCategory),
  )) {
    if (!usage.roleCategory) continue;
    if (!canUseFallbackCategory(usage.roleCategory)) continue;
    addBadge(badges, seen, {
      key: usage.roleCategory,
      label: input.formatLabel(usage.roleCategory),
      icon: badgeIconForKey(usage.roleCategory),
    });
  }

  return badges;
}

function CompanyBadgeList({
  badges,
  hiddenCount,
  number,
}: {
  badges: CompanyBadge[];
  hiddenCount: number;
  number: (value: number) => string;
}) {
  if (badges.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-center gap-1.5">
      {badges.map((badge) => {
        const Icon = badge.icon;
        return (
          <li key={`${badge.key}-${badge.label}`}>
            <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-secondary/50 px-2 py-1 text-xs font-medium text-foreground">
              <Icon className="text-[14px] text-muted-foreground" />
              <span className="truncate">{badge.label}</span>
            </span>
          </li>
        );
      })}
      {hiddenCount > 0 && (
        <li>
          <span className="inline-flex items-center rounded-md border border-border bg-secondary/50 px-2 py-1 text-xs font-medium text-muted-foreground">
            +{number(hiddenCount)}
          </span>
        </li>
      )}
    </ul>
  );
}

function CompanyAliases({
  aliases,
  expanded,
  hiddenCount,
  number,
  onExpandedChange,
  showLess,
  showMore,
}: {
  aliases: string[];
  expanded: boolean;
  hiddenCount: number;
  number: (value: number) => string;
  onExpandedChange: (expanded: boolean) => void;
  showLess: string;
  showMore: string;
}) {
  if (aliases.length === 0) return null;
  return (
    <div className="max-w-3xl text-sm leading-6 text-muted-foreground">
      <ul className="inline">
        {aliases.map((alias, index) => (
          <li key={`${alias}-${index}`} className="inline">
            {index > 0 && <span aria-hidden="true">, </span>}
            <span className="break-words">{alias}</span>
          </li>
        ))}
      </ul>
      {(hiddenCount > 0 || expanded) && " "}
      <span className="inline-flex flex-wrap items-baseline gap-1">
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => onExpandedChange(true)}
            className="rounded border border-border/60 px-2 py-0.5 text-xs font-bold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            +{number(hiddenCount)} {showMore}
          </button>
        )}
        {expanded && aliases.length > ALIAS_PREVIEW_COUNT && (
          <button
            type="button"
            onClick={() => onExpandedChange(false)}
            className="rounded border border-border/60 px-2 py-0.5 text-xs font-bold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            {showLess}
          </button>
        )}
      </span>
    </div>
  );
}

function CompanyIdentityFacts({
  countries,
  foundedLabel,
  foundedYear,
  parentId,
  parentLabel,
  parentName,
}: {
  countries: Array<{ code: string; name: string }>;
  foundedLabel: string | null;
  foundedYear: number | null;
  parentId: string | null;
  parentLabel: string;
  parentName: string | null;
}) {
  const hasFacts = Boolean(foundedLabel || countries.length);
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground md:text-[15px]">
      {parentName && (
        <li>
          {parentLabel}:{" "}
          {parentId ? (
            <Link to={`/company/${parentId}`} className="text-primary hover:underline">
              {parentName}
            </Link>
          ) : (
            parentName
          )}
          {hasFacts && <span aria-hidden="true"> ·</span>}
        </li>
      )}
      {foundedLabel && foundedYear && (
        <li>
          <time dateTime={String(foundedYear)}>{foundedLabel}</time>
          {countries.length > 0 && <span aria-hidden="true"> ·</span>}
        </li>
      )}
      {countries.length > 0 && (
        <li>
          <ul className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
            {countries.map((country) => (
              <li key={country.code} className="inline-flex items-center gap-1.5">
                <CountryFlag code={country.code} decorative />
                <span>{country.name}</span>
              </li>
            ))}
          </ul>
        </li>
      )}
    </ul>
  );
}

function CompanyProfile({
  profile,
  profileHtml,
  wikidataId,
}: Pick<CompanyHeaderProps, "profile" | "profileHtml" | "wikidataId">) {
  if (profileHtml) return <RichTextHtml safeHtml={profileHtml} maxLines={3} />;
  if (profile) return <BioText text={profile} maxLines={3} />;
  if (!wikidataId) return null;
  return <WikipediaSection wikidataId={wikidataId} />;
}

export function CompanyHeader({
  name,
  aliases,
  parentName,
  parentId,
  officialUrls,
  socialUrls,
  imageUrl,
  profile,
  profileHtml,
  wikidataId,
  shortDescription,
  foundedYear,
  countryCodes,
  usageCategories,
  primaryUsageCategory,
  roleUsage,
  taxonomy,
  aside,
}: CompanyHeaderProps) {
  const t = useTranslations("label");
  const tBio = useTranslations("shared.bioText");
  const tCommon = useTranslations("globals.common");
  const locale = useLocale();
  const format = useFormatter();
  const [aliasesExpanded, setAliasesExpanded] = useState(false);
  const countries = countryCodes.map((countryCode) => ({
    code: countryCode,
    name: formatCountry(countryCode, locale),
  }));
  const visibleAliases = aliasesExpanded ? aliases : aliases.slice(0, ALIAS_PREVIEW_COUNT);
  const hiddenAliasCount = aliases.length - visibleAliases.length;
  const formatCompanyUsageLabel = (key: string, fallback?: string) => {
    const translationKey = `companyUsage_${key}` as Parameters<typeof t>[0];
    return t.has(translationKey)
      ? t(translationKey)
      : (fallback ?? formatCatalogTaxonomyLabel(key));
  };
  const badges = buildCompanyBadges({
    usageCategories,
    primaryUsageCategory,
    roleUsage,
    taxonomy,
    formatLabel: formatCompanyUsageLabel,
  });
  const visibleBadges = badges.slice(0, BADGE_PREVIEW_COUNT);
  const hiddenBadgeCount = badges.length - visibleBadges.length;

  return (
    <>
      <Breadcrumbs className="mb-4" label={tCommon("breadcrumbNavigation")}>
        <Link to="/search" className="text-muted-foreground hover:text-primary transition-colors">
          {t("breadcrumbSearch")}
        </Link>
        <span className="font-medium text-primary">{name}</span>
      </Breadcrumbs>

      <EntityHero
        title={name}
        mediaShape="square"
        media={
          <FallbackImage
            src={withImageRenderParams(imageUrl, { width: 320 })}
            srcSet={buildImageSrcSet(imageUrl, [150, 320])}
            alt={name}
            fallback="company"
            priority
            className="rounded-xl"
            sizes="(min-width: 768px) 136px, 80px"
          />
        }
        chips={
          <CompanyBadgeList
            badges={visibleBadges}
            hiddenCount={hiddenBadgeCount}
            number={format.number}
          />
        }
        meta={
          <>
            {shortDescription && (
              <p className="text-sm text-muted-foreground">{shortDescription}</p>
            )}
            <CompanyIdentityFacts
              countries={countries}
              foundedLabel={foundedYear ? t("founded", { year: foundedYear }) : null}
              foundedYear={foundedYear}
              parentId={parentId}
              parentLabel={t("parentLabel")}
              parentName={parentName}
            />
          </>
        }
        actions={
          <>
            <ShareButton title={name} className="shrink-0 [&_.share-button-label]:hidden" />
            <EntityQuickLinks officialUrls={officialUrls} socialUrls={socialUrls} />
          </>
        }
        body={
          <CompanyProfile profile={profile} profileHtml={profileHtml} wikidataId={wikidataId} />
        }
        footer={
          <CompanyAliases
            aliases={visibleAliases}
            expanded={aliasesExpanded}
            hiddenCount={hiddenAliasCount}
            number={format.number}
            onExpandedChange={setAliasesExpanded}
            showLess={tBio("showLess")}
            showMore={tBio("showMore")}
          />
        }
        aside={aside}
      />
    </>
  );
}
